import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from '../components/chrome/TopBar';
import LoadingSpinner from '../components/LoadingSpinner';
import { useUser, useAuth, useDialog } from '../context';
import useContainerWidth from '../hooks/useContainerWidth';
import usePortalEditor from '../hooks/usePortalEditor';
import { RING_ON, SERIF } from '../components/profile/fields/ui';
import PhotosTab from '../components/profile/editor/PhotosTab';
import BasicsTab from '../components/profile/editor/BasicsTab';
import VibeTab from '../components/profile/editor/VibeTab';
import TagsTab from '../components/profile/editor/TagsTab';
import SectionsTab from '../components/profile/editor/SectionsTab';
import PreviewRail from '../components/profile/editor/PreviewRail';

/**
 * PortalEditorPage — /portal/edit, design_handoff_portal_profile_editor "Screen 2" (Oct 2 2026).
 *
 * Edit any part of the Portal Page in place, with a live preview and one save. It replaced the
 * route where EDIT sent people back into the 8-step intake stepper; the stepper is first-time
 * onboarding only now. There is no forced order: five tabs over one in-memory page object. The
 * state and the save live in `hooks/usePortalEditor`, shared with the introduction's edit sheet.
 */

const TABS = [['photos', 'PHOTOS + LAYOUT'], ['basics', 'THE BASICS'], ['vibe', 'VIBE + MUSIC'], ['tags', 'STRENGTHS + TAGS'], ['page', 'SECTIONS + PRIVACY']];
const Icon = { back: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" aria-hidden="true"><path d="M19 12H5M11 6l-6 6 6 6" /></svg> };

export default function PortalEditorPage() {
  const navigate = useNavigate();
  const { profile, walletBalance } = useUser();
  const { user, logout } = useAuth();
  const { showConfirm } = useDialog();
  const rootRef = useRef(null);
  const w = useContainerWidth(rootRef);
  const phone = w < 760;
  const wide = w >= 1100;

  const [tab, setTab] = useState('photos');
  const [device, setDevice] = useState('desktop');
  const [showPrev, setShowPrev] = useState(false);
  const {
    dto, page, upd, dirty, canSave, save, discard, saveState, error, uploading, onUpload, handleState, handleOptions,
  } = usePortalEditor({ onHandleBlocked: () => setTab('basics') });

  const leave = async (to) => {
    if (dirty) {
      const ok = await showConfirm({ title: 'LEAVE WITHOUT SAVING?', message: 'Your changes to this page have not been saved.', confirmLabel: 'LEAVE', cancelLabel: 'STAY', variant: 'danger' });
      if (!ok) return;
    }
    navigate(to);
  };

  const lists = useMemo(() => {
    const events = Array.isArray(dto?.events) ? dto.events : [];
    return {
      going: events.filter((e) => e.relationship === 'going'),
      interested: events.filter((e) => e.relationship === 'interested'),
      hosting: events.filter((e) => e.relationship === 'hosting'),
      clubs: Array.isArray(dto?.clubs) ? dto.clubs : [],
    };
  }, [dto]);

  const handleLogout = async () => { await logout(); navigate('/'); };
  const chrome = <TopBar user={profile || user} walletBalance={walletBalance} onLogout={handleLogout} onLogoClick={() => leave('/featured')} />;

  if (!page) {
    return (
      <div className="pk" style={{ minHeight: '100dvh', background: '#000', color: '#fff' }}>
        {chrome}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '96px 16px' }}><LoadingSpinner label="Opening your page..." /></div>
      </div>
    );
  }

  const myPage = `/${page.handle || dto?.handle || ''}`;
  const av = page.photos.find((p) => p.id === page.avatar) || page.photos[0];
  const status = saveState === 'saved' ? ['SAVED', '#22ECFB', '#000'] : dirty ? ['UNSAVED CHANGES', '#FFCCE6', '#000'] : ['ALL CHANGES SAVED', '#161616', '#8A8A8A'];
  const saveLabel = saveState === 'saving' ? 'SAVING…' : saveState === 'saved' ? 'SAVED' : 'SAVE CHANGES';
  const r = phone ? 22 : 28;
  const gap = phone ? 12 : 16;
  const tabProps = { d: page, upd, phone };

  const saveButton = (height) => (
    <button type="button" onClick={save} disabled={!canSave} data-testid="save-button" className="pk-btn" style={{ height, flex: phone ? 1 : undefined, background: canSave ? '#fff' : '#161616', color: canSave ? '#000' : '#6B6B6B', fontSize: phone ? 11 : 12, fontWeight: 800, letterSpacing: '.16em', opacity: 1, cursor: canSave ? 'pointer' : 'default' }}>{saveLabel}</button>
  );

  return (
    <div ref={rootRef} className="pk" data-testid="portal-editor" style={{ minHeight: '100dvh', background: '#000', color: '#fff', overflowX: 'clip' }}>
      {chrome}
      <main style={{ maxWidth: 1280, margin: '0 auto', padding: phone ? '16px 16px 24px' : '28px 32px 72px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <button type="button" onClick={() => leave(myPage)} className="pk-btn pk-secondary-on-black" style={{ height: 40, padding: '0 16px 0 12px', fontSize: 11, fontWeight: 700, letterSpacing: '.14em' }}>{Icon.back}MY PORTAL PAGE</button>
          <span data-testid="save-status" style={{ padding: '6px 12px', borderRadius: 999, background: status[1], color: status[2], fontSize: 10, fontWeight: 800, letterSpacing: '.16em' }}>{status[0]}</span>
        </div>

        <section style={{ display: 'flex', alignItems: 'center', gap: 18, padding: phone ? 16 : '20px 24px', borderRadius: r, background: '#111', boxShadow: 'inset 0 0 0 1px #1F1F1F' }}>
          <span style={{ flexShrink: 0, width: phone ? 64 : 80, height: phone ? 64 : 80, borderRadius: 18, overflow: 'hidden', background: '#000' }}>
            {av && <img src={av.src} alt="" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: `${page.ax}% ${page.ay}%`, display: 'block' }} />}
          </span>
          <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.18em', color: '#8A8A8A' }}>EDIT PORTAL PAGE</span>
            <h1 style={{ margin: 0, fontFamily: SERIF, fontWeight: 700, fontSize: phone ? 26 : 34, lineHeight: 1.05, textTransform: 'uppercase', overflowWrap: 'anywhere' }}>{page.name || dto?.handle}</h1>
            <span style={{ fontSize: 12, lineHeight: 1.55, color: '#8A8A8A' }}>Changes show in the preview as you make them. Nothing goes live until you save.</span>
          </span>
        </section>

        <div role="tablist" className={phone ? 'pk-scroll-x' : ''} style={{ display: 'flex', flexWrap: phone ? 'nowrap' : 'wrap', gap: 6 }}>
          {TABS.map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={k === tab} onClick={() => setTab(k)} className="pk-btn" style={{ flexShrink: 0, height: 40, padding: '0 18px', border: '2px solid transparent', background: k === tab ? RING_ON : '#111', color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: '.14em' }}>{l}</button>
          ))}
        </div>

        {error && (
          <div role="alert" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, padding: '12px 16px', borderRadius: 18, background: '#FEE2E2', color: '#991B1B' }}>
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.16em' }}>COULD NOT SAVE</span>
            <span style={{ fontSize: 12 }}>{error}</span>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: wide ? 'minmax(0,1fr) 440px' : 'minmax(0,1fr)', gap, alignItems: 'start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap, minWidth: 0 }}>
            {tab === 'photos' && <PhotosTab {...tabProps} device={device} setDevice={setDevice} onUpload={onUpload} uploading={uploading} />}
            {tab === 'basics' && <BasicsTab {...tabProps} handleState={handleState} handleOptions={handleOptions} />}
            {tab === 'vibe' && <VibeTab {...tabProps} />}
            {tab === 'tags' && <TagsTab {...tabProps} />}
            {tab === 'page' && <SectionsTab {...tabProps} />}
          </div>

          <div style={{ order: phone ? -1 : 0, position: wide ? 'sticky' : 'static', top: 88, display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
            {phone && (
              <button type="button" onClick={() => setShowPrev((v) => !v)} className="pk-btn pk-secondary-on-black" style={{ height: 44, fontSize: 11, fontWeight: 700, letterSpacing: '.14em' }}>{showPrev ? 'HIDE PREVIEW' : 'SHOW PREVIEW'}</button>
            )}
            {(!phone || showPrev) && <PreviewRail d={page} device={device} setDevice={setDevice} lists={lists} />}
            {!phone && (
              <>
                {saveButton(54)}
                {dirty && <button type="button" onClick={discard} className="pk-btn pk-secondary-on-black" style={{ height: 44, fontSize: 11, fontWeight: 700, letterSpacing: '.14em' }}>DISCARD CHANGES</button>}
                <button type="button" onClick={() => leave(myPage)} className="pk-btn pk-secondary-on-black" style={{ height: 44, fontSize: 11, fontWeight: 700, letterSpacing: '.14em' }}>VIEW MY PAGE →</button>
              </>
            )}
          </div>
        </div>
      </main>

      {phone && (
        <div style={{ position: 'sticky', bottom: 0, zIndex: 40, display: 'flex', gap: 8, padding: '12px 16px', paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))', background: '#000', boxShadow: '0 -1px 0 #1F1F1F' }}>
          {dirty && <button type="button" onClick={discard} className="pk-btn pk-secondary-on-black" style={{ flex: '0 0 auto', height: 50, padding: '0 18px', fontSize: 10, fontWeight: 700, letterSpacing: '.14em' }}>DISCARD</button>}
          {saveButton(50)}
        </div>
      )}
    </div>
  );
}
